"use client";

import * as React from "react";
import BigNumber from "bignumber.js";
import { parseNumeric } from "../../../lib/numeric";
import type { Environment } from "../../../types";
import type { TokenBalance, TokenInfo, TokenStats } from "../types";
import { useTokenHistory } from "../queries/use-token-history";
import { useTokenDetailVisibility } from "../queries/use-token-detail-visibility";
import { TokenPriceChart } from "./TokenPriceChart";
import { useExplorerUrlBuilder } from "../../send/queries/use-chain-info";
import { useChainDisplay } from "../../../lib/remote-config";
import {
  formatWei,
  formatTokenValue,
  shortenAddress,
} from "../../../lib/format";
import { cn } from "../../../lib/utils";
import { handleOutlinkClick, type OnOutlink } from "../../../lib/outlink";
import {
  BackIcon,
  CloseIcon,
  CopyIcon,
  CheckIcon,
} from "./icons";

export interface TokenDetailViewProps {
  env: Environment;
  /** 모바일(전체화면)은 무한스크롤, PC(팝오버)는 "Load more" 버튼으로 페이지네이션. */
  isMobile: boolean;
  token: TokenBalance;
  walletAddress: string;
  stat?: TokenStats;
  tokenInfo?: TokenInfo;
  showBalance?: boolean;
  onBack: () => void;
  onClose: () => void;
  onSend: (token: TokenBalance) => void;
  onOutlink?: OnOutlink;
}

const FALLBACK_TOKEN_ICON =
  "https://contents.crosstoken.io/wallet/token/images/FT.png";

/** 이력 항목의 자금 흐름 방향. bridge-*는 브릿지 컨트랙트가 상대일 때. */
type TokenHistoryDirection = "receive" | "send" | "bridge-in" | "bridge-out";

const HISTORY_DIRECTION_LABEL: Record<TokenHistoryDirection, string> = {
  receive: "Receive",
  send: "Send",
  "bridge-in": "Bridge In",
  "bridge-out": "Bridge Out",
};

/**
 * 이력 금액이 길어 한 줄을 넘기지 않도록 글자 수 기준으로 폰트 크기 티어를 고른다.
 * (부호 + 금액 + 심볼 전체 길이 기준. CSS에서 `data-size`별 font-size로 매핑.)
 */
function historyAmountSize(text: string): "md" | "sm" | "xs" {
  if (text.length > 24) return "xs";
  if (text.length > 18) return "sm";
  return "md";
}

/** unix timestamp(초 또는 밀리초) → "3m" / "5h" / "2d" 형태의 상대 시간. */
function formatRelativeTime(timestamp: number): string {
  if (!timestamp) return "";
  const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
  const diffSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (diffSec < 60) return "just now";
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `about ${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d`;
  const month = Math.floor(day / 30);
  if (month < 12) return `${month}mo`;
  return `${Math.floor(month / 12)}y`;
}

/**
 * 정수부에 천단위 콤마를 넣는다. `formatRawTokenAmount` 결과(콤마 없음) 보정용.
 * 정규식(백트래킹) 대신 선형 루프로 처리 — 코드베이스의 ReDoS 회피 관례를 따른다.
 */
function groupInteger(value: string): string {
  const [intPart, fracPart] = value.split(".");
  let grouped = "";
  for (let i = 0; i < intPart.length; i++) {
    const fromEnd = intPart.length - i;
    if (i > 0 && fromEnd % 3 === 0) grouped += ",";
    grouped += intPart[i];
  }
  return fracPart ? `${grouped}.${fracPart}` : grouped;
}

/**
 * raw(wei-scale) 토큰 금액을 소수점 2자리까지 **버림(floor)** 하여 표기한다.
 * 잔액은 음수가 없으므로 BigInt 나눗셈의 0 방향 절사 = floor. 전 과정을
 * BigInt로 유지하고(정밀도 손실 방지), 항상 2자리로 고정 표기한다.
 */
function floorTokenAmount2(raw: string, decimals: number): string {
  const value = BigInt(raw || "0");
  if (decimals <= 2) {
    const unit = 10n ** BigInt(decimals);
    const whole = value / unit;
    const cents = (value % unit) * 10n ** BigInt(2 - decimals);
    return `${whole.toString()}.${cents.toString().padStart(2, "0")}`;
  }
  const hundredths = value / 10n ** BigInt(decimals - 2); // 2자리 초과분 버림
  const whole = hundredths / 100n;
  const cents = hundredths % 100n;
  return `${whole.toString()}.${cents.toString().padStart(2, "0")}`;
}

/**
 * 텍스트가 컨테이너 폭을 넘으면 폰트 크기를 줄여 항상 한 줄에 맞춘다.
 * balance 처럼 길이가 가변적인 수치를 줄바꿈 없이 표기할 때 사용.
 *
 * 컨테이너 폭은 flex 레이아웃이 결정하므로(폰트 크기와 무관) 항상 최대
 * 크기에서 자연 폭을 측정한 뒤 축소 배율을 계산한다 — 진동/루프가 없다.
 */
function useFitFontSize(text: string, maxPx: number, minPx: number) {
  const containerRef = React.useRef<HTMLElement | null>(null);
  const textRef = React.useRef<HTMLElement | null>(null);
  const [fontSize, setFontSize] = React.useState(maxPx);

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    const el = textRef.current;
    if (!container || !el) return;

    const measure = () => {
      const available = container.clientWidth;
      if (!available) {
        // 아직 레이아웃 전(폭 0). ResizeObserver가 폭이 잡히면 다시 부른다.
        return;
      }
      // 자연 폭은 flex/max-width clamp 에 영향받지 않도록 body 에 붙인 분리형
      // probe 로 잰다. el 을 직접 재면 stretch·max-width:100% 때문에 폭이 잘려
      // scrollWidth 가 과소 보고된다.
      const cs = window.getComputedStyle(el);
      const probe = document.createElement("span");
      probe.textContent = el.textContent;
      probe.style.cssText = [
        "position:absolute",
        "left:-9999px",
        "top:-9999px",
        "visibility:hidden",
        "white-space:nowrap",
        `font-family:${cs.fontFamily}`,
        `font-weight:${cs.fontWeight}`,
        `letter-spacing:${cs.letterSpacing}`,
        `font-size:${maxPx}px`,
      ].join(";");
      document.body.appendChild(probe);
      const natural = probe.offsetWidth;
      document.body.removeChild(probe);

      if (natural <= available) {
        setFontSize(maxPx);
        return;
      }
      const next = Math.max(minPx, Math.floor((maxPx * available) / natural));
      setFontSize(next);
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [text, maxPx, minPx]);

  return { containerRef, textRef, fontSize };
}

/** circulating_supply(이미 토큰 단위 소수 문자열)를 콤마 + 최대 4자리 절사로 표기. */
function formatSupply(value: string | undefined): string {
  // `isNaN()` 검사로는 못 막는다 — 숫자가 아닌 문자열이면 생성자가 먼저 던진다.
  const bn = parseNumeric(value);
  if (!bn) return "-";
  return bn.decimalPlaces(4, BigNumber.ROUND_DOWN).toFormat();
}

interface SocialLink {
  key: string;
  label: string;
  url: string;
  icon: React.ReactNode;
}

function GlobeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M3 12h18M12 3c2.5 2.5 3.8 5.8 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.8-3.8-9S9.5 5.5 12 3Z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function TelegramIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M21.9 4.3 18.6 20c-.2 1-.9 1.3-1.8.8l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.3-4.9 9-8.1c.4-.3-.1-.5-.6-.2L6.2 13.2l-4.8-1.5c-1-.3-1-1 .2-1.5L20.6 3c.9-.3 1.6.2 1.3 1.3Z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.9 2H22l-7.3 8.3L23.3 22h-6.8l-5.3-6.9L5.1 22H2l7.8-8.9L1 2h6.9l4.8 6.4L18.9 2Zm-1.2 18h1.9L7.2 4H5.2l12.5 16Z" />
    </svg>
  );
}

function MediumIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M6.9 6.5c0-.2-.1-.5-.3-.6L4.6 3.6V3.3h6.3l4.9 10.7 4.3-10.7H26v.3l-1.8 1.7c-.2.1-.2.3-.2.5v12.4c0 .2 0 .4.2.5l1.7 1.7v.3h-8.7v-.3l1.8-1.7c.2-.2.2-.2.2-.5V8.1l-5 12.6h-.7L7.9 8.1v8.5c0 .3.1.6.3.8l2.3 2.8v.3H4v-.3l2.3-2.8c.2-.2.3-.5.3-.8V6.5Z" transform="scale(0.85)" />
    </svg>
  );
}

export function TokenDetailView({
  env,
  isMobile,
  token,
  walletAddress,
  stat,
  tokenInfo,
  showBalance = true,
  onBack,
  onClose,
  onSend,
  onOutlink,
}: TokenDetailViewProps) {
  const decimals = token.quantity.decimals;
  const [copied, setCopied] = React.useState(false);

  const { buildTxUrl, chainInfo } = useExplorerUrlBuilder(
    env,
    token.chainId,
    true,
  );

  // 섹션별 노출 여부(S3 wallet-info.json 원격 설정). 미로딩/실패 시 전부 노출.
  const { visibility } = useTokenDetailVisibility(env);

  const {
    data,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchHistory,
  } = useTokenHistory(env, token.chainId, token.address, walletAddress, true);

  const historyItems = React.useMemo(
    () => (data?.pages ? data.pages.flat() : []),
    [data],
  );

  // 무한 스크롤(모바일 전용): 센티넬이 뷰포트에 들어오면 다음 페이지를 로드.
  // PC(팝오버)에서는 관찰하지 않고 "Load more" 버튼으로 대체한다.
  const loadMoreRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!isMobile) return;
    const el = loadMoreRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "120px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleCopyAddress = React.useCallback(() => {
    if (!navigator?.clipboard) return;
    navigator.clipboard
      .writeText(token.address)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }, [token.address]);

  const balanceText = groupInteger(
    floorTokenAmount2(token.quantity.numeric, decimals),
  );
  const fiatText = stat?.price
    ? formatTokenValue(token.quantity.numeric, decimals, stat.price)
    : null;

  // 잔액이 길어 카드 폭을 넘으면 줄바꿈 대신 폰트 크기를 줄여 한 줄로 맞춘다.
  const balanceDisplay = showBalance ? balanceText : "••••";
  const {
    containerRef: balanceLeftRef,
    textRef: balanceAmountRef,
    fontSize: balanceFontSize,
  } = useFitFontSize(balanceDisplay, 30, 14);

  // S3(chains.json) 표시명이 최우선 override, 없으면 백엔드 chainInfo.name,
  // 그것도 없으면 chainId 폴백.
  const chainDisplay = useChainDisplay(token.chainId, env);
  const networkName =
    chainDisplay.name ?? chainInfo?.name ?? `Chain ${token.chainId}`;
  const explorerBase = chainInfo?.explorer_url;
  const deployed = tokenInfo?.deployed ?? true;

  const socialLinks = React.useMemo<SocialLink[]>(() => {
    const links: SocialLink[] = [];
    if (tokenInfo?.info_url)
      links.push({ key: "web", label: "Website", url: tokenInfo.info_url, icon: <GlobeIcon /> });
    if (tokenInfo?.telegram_url)
      links.push({ key: "tg", label: "Telegram", url: tokenInfo.telegram_url, icon: <TelegramIcon /> });
    if (tokenInfo?.x_url)
      links.push({ key: "x", label: "X", url: tokenInfo.x_url, icon: <XIcon /> });
    if (tokenInfo?.medium_url)
      links.push({ key: "medium", label: "Medium", url: tokenInfo.medium_url, icon: <MediumIcon /> });
    return links;
  }, [tokenInfo]);

  const outlink = (
    e: React.MouseEvent<HTMLAnchorElement>,
    url: string,
    origin: "token-network" | "token-social" | "token-tx",
    txHash?: string,
  ) => {
    handleOutlinkClick(
      e,
      url,
      {
        category: "token-detail",
        origin,
        payload: { address: token.address, txHash },
      },
      onOutlink,
    );
  };

  return (
    <>
      <div className="wi-sticky-header">
        <div className="wi-header wi-header--receive">
          <button
            type="button"
            className="wi-header-back"
            onClick={onBack}
            aria-label="Back"
          >
            <BackIcon size={24} />
          </button>
          <span className="wi-td-header-title">
            {/* icon_url 원본 이미지에 체인 배지가 이미 합성돼 있어 별도 오버레이는 두지 않는다. */}
            <img
              src={token.icon_url}
              alt=""
              aria-hidden
              className="wi-td-header-icon"
              width={24}
              height={24}
              draggable={false}
              onError={(e) => {
                e.currentTarget.src = FALLBACK_TOKEN_ICON;
              }}
            />
            <span className="wi-td-header-name">{token.symbol}</span>
          </span>
          <button
            type="button"
            className="wi-header-action"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon size={24} />
          </button>
        </div>
        <hr className="wi-divider" />
      </div>

      <div className="wi-td-scroll">
        {/* Price chart (자체 SVG, 데이터 없으면 자동 숨김) */}
        {visibility.chart && (
          <TokenPriceChart
            env={env}
            chainId={token.chainId}
            tokenAddress={token.address}
          />
        )}

        {/* My Balance */}
        {visibility.balance && (
        <section className="wi-td-balance-card">
          <div
            className="wi-td-balance-left"
            ref={balanceLeftRef as React.RefObject<HTMLDivElement>}
          >
            <span className="wi-td-balance-label">My Balance</span>
            <span
              className="wi-td-balance-amount"
              ref={balanceAmountRef as React.RefObject<HTMLSpanElement>}
              style={{ fontSize: balanceFontSize }}
            >
              {balanceDisplay}
            </span>
            {showBalance && fiatText && (
              <span className="wi-td-balance-fiat">{fiatText}</span>
            )}
          </div>
          <button
            type="button"
            className="wi-td-send-btn"
            onClick={() => onSend(token)}
            aria-label={`Send ${token.symbol}`}
            data-track="token_detail_send"
            data-track-id={token.symbol}
          >
            <span>Send</span>
          </button>
        </section>
        )}

        {/* Crypto Overview */}
        {visibility.overview && (
        <section className="wi-td-section">
          <h3 className="wi-td-section-title">Crypto Overview</h3>
          <div className="wi-td-rows">
            <div className="wi-td-row">
              <span className="wi-td-row-label">Network</span>
              {explorerBase ? (
                <a
                  className="wi-td-row-value wi-td-row-link"
                  href={explorerBase}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => outlink(e, explorerBase, "token-network")}
                >
                  <span>{networkName}</span>
                  <ExternalLinkIcon />
                </a>
              ) : (
                <span className="wi-td-row-value">{networkName}</span>
              )}
            </div>

            <div className="wi-td-row">
              <span className="wi-td-row-label">Contract Address</span>
              <button
                type="button"
                className="wi-td-row-value wi-td-row-copy"
                onClick={handleCopyAddress}
                aria-label="Copy contract address"
              >
                <span>{shortenAddress(token.address)}</span>
                {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
              </button>
            </div>

            <div className="wi-td-row">
              <span className="wi-td-row-label">Decimals</span>
              <span className="wi-td-row-value">{decimals}</span>
            </div>

            <div className="wi-td-row">
              <span className="wi-td-row-label">Total Supply</span>
              <span className="wi-td-row-value">
                {formatSupply(stat?.circulating_supply)}
              </span>
            </div>
          </div>
        </section>
        )}

        {/* Social Media */}
        {visibility.social && socialLinks.length > 0 && (
          <section className="wi-td-section">
            <h3 className="wi-td-section-title">Social Media</h3>
            <div className="wi-td-social-row">
              {socialLinks.map((link) => (
                <a
                  key={link.key}
                  className="wi-td-social-btn"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.label}
                  onClick={(e) => outlink(e, link.url, "token-social")}
                >
                  {link.icon}
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Token History */}
        {visibility.history && (
        <section className="wi-td-section">
          <h3 className="wi-td-section-title">Token History</h3>

          {isHistoryLoading && (
            <div className="wi-td-history-list">
              {Array.from({ length: 3 }, (_, i) => (
                <div key={`td-sk-${i}`} className="wi-td-history-skeleton">
                  <div className="wi-skeleton-block" style={{ width: 90, height: 14 }} />
                  <div className="wi-skeleton-block" style={{ width: 60, height: 14 }} />
                </div>
              ))}
            </div>
          )}

          {!isHistoryLoading && isHistoryError && (
            <div className="wi-td-history-empty">
              <span>Failed to load history.</span>
              <button
                type="button"
                className="wi-td-retry-btn"
                onClick={() => refetchHistory()}
              >
                Retry
              </button>
            </div>
          )}

          {!isHistoryLoading && !isHistoryError && historyItems.length === 0 && (
            <div className="wi-td-history-empty">
              <span>No transactions found.</span>
            </div>
          )}

          {!isHistoryLoading && !isHistoryError && historyItems.length > 0 && (
            <div className="wi-td-history-list">
              {historyItems.map((item, idx) => {
                // 브릿지 판별을 먼저 한다: 백엔드가 브릿지 컨트랙트에 alias를
                // 붙여주면 to_alias/from_alias에 "bridge"가 담겨 온다. alias가
                // 없으면 자연히 Send/Receive로 폴백된다.
                const toIsBridge =
                  item.to_alias?.toLowerCase().includes("bridge") ?? false;
                const fromIsBridge =
                  item.from_alias?.toLowerCase().includes("bridge") ?? false;
                const direction: TokenHistoryDirection = toIsBridge
                  ? "bridge-out"
                  : fromIsBridge
                    ? "bridge-in"
                    : item.to?.toLowerCase() === walletAddress.toLowerCase()
                      ? "receive"
                      : "send";
                const isInflow =
                  direction === "receive" || direction === "bridge-in";
                const counterparty = isInflow ? item.from : item.to;
                const amount = groupInteger(
                  floorTokenAmount2(item.amount, decimals),
                );
                const txUrl = buildTxUrl(item.tx);
                const relative = formatRelativeTime(item.timestamp);

                const rowInner = (
                  <>
                    <div className="wi-td-history-top">
                      <span
                        className="wi-td-history-type"
                        data-direction={direction}
                      >
                        {HISTORY_DIRECTION_LABEL[direction]}
                      </span>
                      <span className="wi-td-history-counter">
                        {shortenAddress(counterparty ?? "")}
                      </span>
                    </div>
                    <div className="wi-td-history-bottom">
                      <span className="wi-td-history-meta">
                        <span
                          className="wi-td-history-status"
                          data-status={item.status}
                        >
                          {item.status === "success" ? "Success" : "Failed"}
                        </span>
                        {relative && (
                          <span className="wi-td-history-time">{relative}</span>
                        )}
                      </span>
                      <span
                        className="wi-td-history-amount"
                        data-size={historyAmountSize(
                          `${isInflow ? "+" : "-"}${amount} ${token.symbol}`,
                        )}
                      >
                        {isInflow ? "+" : "-"}
                        {amount} {token.symbol}
                      </span>
                    </div>
                  </>
                );

                const key = `${item.tx}-${item.log_index}-${idx}`;
                return txUrl ? (
                  <a
                    key={key}
                    className="wi-td-history-item wi-td-history-item--link"
                    href={txUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => outlink(e, txUrl, "token-tx", item.tx)}
                  >
                    {rowInner}
                  </a>
                ) : (
                  <div key={key} className="wi-td-history-item">
                    {rowInner}
                  </div>
                );
              })}

              {hasNextPage &&
                (isMobile ? (
                  <div ref={loadMoreRef} className="wi-td-history-sentinel">
                    {isFetchingNextPage && (
                      <span className="wi-td-history-loading">Loading…</span>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="wi-td-loadmore-btn"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? "Loading…" : "Load more"}
                  </button>
                ))}
            </div>
          )}
        </section>
        )}
      </div>
    </>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      className={cn("wi-td-external-icon")}
      aria-hidden
    >
      <path
        d="M14 4h6v6M20 4l-9 9M18 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
